import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCopy, faPaperPlaneTop, faShieldHalved} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent, BicInputComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {ConnectStartView, ExternalConnectionControllerService, ExternalConnectionView} from '../../../../api';
import {NotificationsService} from '../../../../services/notifications/notifications.service';

export interface ConnectSharePointDialogData {
  organisationId: number;
  connection: ExternalConnectionView | undefined;
}

type Approver = 'self' | 'it';

/**
 * Verbinden mit SharePoint (Figma Popup 1/2): Berechtigungen erklären, dann entweder selbst bei Microsoft
 * zustimmen (Weiterleitung) oder den Zustimmungslink an die IT schicken. Jede neue URL macht die vorherige
 * ungültig (Backend).
 */
@Component({
  selector: 'app-connect-sharepoint-dialog',
  templateUrl: './connect-sharepoint-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BicPopupComponent, BicButtonComponent, BicInputComponent, FaIconComponent, FormsModule, DatePipe]
})
export class ConnectSharePointDialogComponent {
  readonly data = inject<ConnectSharePointDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<ConnectSharePointDialogComponent>>(MatDialogRef);
  private readonly connectionService = inject(ExternalConnectionControllerService);
  private readonly notifications = inject(NotificationsService);
  private readonly cdr = inject(ChangeDetectorRef);

  approver: Approver = this.data.connection?.status === 'PENDING_CONSENT' ? 'it' : 'self';
  email = '';
  busy = false;
  /** Gesetzt, sobald der Link an die IT verschickt wurde. */
  sent: ConnectStartView | undefined;

  protected readonly faShieldHalved = faShieldHalved;
  protected readonly faCopy = faCopy;
  protected readonly faPaperPlaneTop = faPaperPlaneTop;

  continue(): void {
    this.busy = true;
    this.connectionService.startExternalConnection({provider: 'SHAREPOINT'}, this.data.organisationId).subscribe({
      next: start => {
        if (this.approver === 'self') {
          this.redirect(start.consentUrl!);
          return;
        }
        this.sendMail(start.connection!.id!);
      },
      error: err => this.fail(err, 'Die Verbindung konnte nicht gestartet werden.')
    });
  }

  copyLink(): void {
    if (!this.sent?.consentUrl) {
      return;
    }
    navigator.clipboard.writeText(this.sent.consentUrl).then(
      () => this.notifications.showSuccessMessage('Link kopiert'),
      () => this.notifications.showErrorMessage('Der Link konnte nicht kopiert werden.'));
  }

  close(): void {
    this.dialogRef.close(!!this.sent);
  }

  private sendMail(connectionId: string): void {
    this.connectionService.sendExternalConnectionConsentMail({email: this.email.trim()}, this.data.organisationId, connectionId)
      .subscribe({
        next: start => {
          this.sent = start;
          this.busy = false;
          this.notifications.showSuccessMessage(`Zustimmungslink an ${this.email.trim()} gesendet`);
          this.cdr.markForCheck();
        },
        error: err => this.fail(err, 'Die Mail konnte nicht gesendet werden.')
      });
  }

  private fail(err: { error?: { message?: string } }, fallback: string): void {
    this.busy = false;
    this.notifications.showErrorMessage(err?.error?.message ?? fallback);
    this.cdr.markForCheck();
  }

  /** Verlässt planBIC Richtung Microsoft-Login; eigene Methode, damit Tests die Weiterleitung abfangen können. */
  redirect(url: string): void {
    window.location.assign(url);
  }
}
